import type { ViewportTransform } from "../coordinates/sourceToViewport";
import type { NormPointTuple, Shape, ShapeGeometry } from "./shapes";

/**
 * strokes - pure geometry helpers for the annotation canvas (D-01, D-08).
 *
 * Everything here operates on NormPointTuple ([x, y] in 0..1 against the unmirrored canonical
 * source, per D-05) or on pixel positions produced by a caller-supplied ViewportTransform; no
 * DOM or canvas access happens in this file.
 */

const QUANTIZE_DECIMALS = 4;
const FREEHAND_START_EPSILON = 0.002;

export function clampPoint(p: NormPointTuple): NormPointTuple {
  return [Math.min(1, Math.max(0, p[0])), Math.min(1, Math.max(0, p[1]))];
}

export function quantizePoint(p: NormPointTuple, decimals: number = QUANTIZE_DECIMALS): NormPointTuple {
  const factor = 10 ** decimals;
  return [Math.round(p[0] * factor) / factor, Math.round(p[1] * factor) / factor];
}

// Perpendicular distance from p to the infinite line through a and b (the classic RDP metric,
// not clamped to the segment) - in normalized space, same units as epsilon.
function perpendicularDistance(p: NormPointTuple, a: NormPointTuple, b: NormPointTuple): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.hypot(p[0] - a[0], p[1] - a[1]);
  }
  const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
  const cx = a[0] + t * dx;
  const cy = a[1] + t * dy;
  return Math.hypot(p[0] - cx, p[1] - cy);
}

/**
 * Ramer-Douglas-Peucker in normalized space. Keeps the first and last point always; iterative
 * (explicit stack) rather than recursive so a straight run of thousands of points cannot blow
 * the call stack.
 */
export function simplifyStroke(points: NormPointTuple[], epsilon: number): NormPointTuple[] {
  if (points.length <= 2) {
    return points.slice();
  }

  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  const stack: Array<[number, number]> = [[0, points.length - 1]];
  while (stack.length > 0) {
    const [start, end] = stack.pop()!;
    if (end - start < 2) {
      continue;
    }
    const a = points[start];
    const b = points[end];
    let maxDist = -1;
    let maxIdx = -1;
    for (let i = start + 1; i < end; i++) {
      const dist = perpendicularDistance(points[i], a, b);
      if (dist > maxDist) {
        maxDist = dist;
        maxIdx = i;
      }
    }
    if (maxDist > epsilon && maxIdx !== -1) {
      keep[maxIdx] = true;
      stack.push([start, maxIdx]);
      stack.push([maxIdx, end]);
    }
  }

  return points.filter((_, i) => keep[i]);
}

/**
 * Prepares a raw freehand stroke for the wire (D-01): clamp into the image, quantize to 4
 * decimals, drop consecutive duplicates, then simplify - doubling epsilon from 0.002 until the
 * result is at most maxPoints. Always returns at least 2 points.
 */
export function prepareFreehand(points: NormPointTuple[], maxPoints = 400): NormPointTuple[] {
  const prepared = points.map((p) => quantizePoint(clampPoint(p)));

  const deduped: NormPointTuple[] = [];
  for (const p of prepared) {
    const last = deduped[deduped.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) {
      deduped.push(p);
    }
  }

  if (deduped.length === 0) {
    return [
      [0, 0],
      [0, 0],
    ];
  }
  if (deduped.length === 1) {
    return [deduped[0], deduped[0]];
  }

  let epsilon = FREEHAND_START_EPSILON;
  let simplified = simplifyStroke(deduped, epsilon);
  while (simplified.length > maxPoints) {
    epsilon *= 2;
    simplified = simplifyStroke(deduped, epsilon);
  }

  if (simplified.length < 2) {
    return [deduped[0], deduped[deduped.length - 1]];
  }
  return simplified;
}

function distToSegment(p: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    return Math.hypot(p.x - a.x, p.y - a.y);
  }
  const t = Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
  const cx = a.x + t * dx;
  const cy = a.y + t * dy;
  return Math.hypot(p.x - cx, p.y - cy);
}

/**
 * Pixel-space hit test through the shared transform (never in normalized space, since tolerance
 * is a screen quantity). Arrows and freehand strokes are tested as a polyline; a rect is tested
 * as its 4 edges only - the interior is never a hit (outline-only, matching how it is drawn).
 */
export function hitTestShape(
  shape: Shape,
  px: { x: number; y: number },
  transform: ViewportTransform,
  tolerancePx: number,
): boolean {
  const viewportPoints = shape.points.map((p) => transform.toViewport({ x: p[0], y: p[1] }));

  if (shape.tool === "rect") {
    if (viewportPoints.length < 2) {
      return false;
    }
    const [p0, p1] = viewportPoints;
    const minX = Math.min(p0.x, p1.x);
    const maxX = Math.max(p0.x, p1.x);
    const minY = Math.min(p0.y, p1.y);
    const maxY = Math.max(p0.y, p1.y);
    const corners = [
      { x: minX, y: minY },
      { x: maxX, y: minY },
      { x: maxX, y: maxY },
      { x: minX, y: maxY },
    ];
    for (let i = 0; i < corners.length; i++) {
      const a = corners[i];
      const b = corners[(i + 1) % corners.length];
      if (distToSegment(px, a, b) <= tolerancePx) {
        return true;
      }
    }
    return false;
  }

  // arrow and freehand: a polyline through every stored point.
  for (let i = 0; i < viewportPoints.length - 1; i++) {
    if (distToSegment(px, viewportPoints[i], viewportPoints[i + 1]) <= tolerancePx) {
      return true;
    }
  }
  return false;
}

/**
 * Shifts every point of a shape by (dx, dy) in normalized units, clamped as a whole so the
 * shape's bounding box never leaves [0, 1] on either axis - a shape dragged past the edge stops
 * at the edge without distorting (every point keeps its offset from every other point).
 */
export function translateGeometry(geometry: ShapeGeometry, dx: number, dy: number): ShapeGeometry {
  const xs = geometry.points.map((p) => p[0]);
  const ys = geometry.points.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  const clampedDx = Math.min(Math.max(dx, -minX), 1 - maxX);
  const clampedDy = Math.min(Math.max(dy, -minY), 1 - maxY);

  return {
    ...geometry,
    points: geometry.points.map((p) => quantizePoint([p[0] + clampedDx, p[1] + clampedDy])),
  };
}
